// src/theme/index.ts
import { createTheme, Theme } from '@mui/material/styles';

// Extend the Material-UI theme to include custom colors
declare module '@mui/material/styles' {
  interface Palette {
    requestBadge: Palette['primary'];
    customOrange: Palette['primary'];
    customGreen: Palette['primary'];
    reliablyBlue: Palette['primary'];
    blueGrey: Palette['primary'];
    reliablyOrange: Palette['primary'];
    customGrey: Palette['primary'];
    lightGrey: Palette['primary'];
    darkGrey: Palette['primary'];
    black: Palette['primary'];
    spinachGreen: Palette['primary'];
    lightOrange: Palette['primary'];
    lightRed: Palette['primary'];
    darkRed: Palette['primary'];
    lightGreen: Palette['primary'];
    darkGreen: Palette['primary'];
    lightBlue: Palette['primary'];
    lightYellow: Palette['primary'];
    darkYellow: Palette['primary'];
  }
  interface PaletteOptions {
    requestBadge?: PaletteOptions['primary'];
    customOrange?: PaletteOptions['primary'];
    customGreen?: PaletteOptions['primary'];
    reliablyBlue?: PaletteOptions['primary'];
    blueGrey?: PaletteOptions['primary'];
    reliablyOrange?: PaletteOptions['primary'];
    customGrey?: PaletteOptions['primary'];
    lightGrey?: PaletteOptions['primary'];
    darkGrey?: PaletteOptions['primary'];
    black?: PaletteOptions['primary'];
    spinachGreen?: PaletteOptions['primary'];
    lightOrange?: PaletteOptions['primary'];
    lightRed?: PaletteOptions['primary'];
    darkRed?: PaletteOptions['primary'];
    lightGreen?: PaletteOptions['primary'];
    darkGreen?: PaletteOptions['primary'];
    lightBlue?: PaletteOptions['primary'];
    lightYellow?: PaletteOptions['primary'];
    darkYellow?: PaletteOptions['primary'];
  }
}

// Extend the component theme to include custom colors in components
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    requestBadge: true;
    customOrange: true;
    customGreen: true;
    reliablyBlue: true;
    blueGrey: true;
    reliablyOrange: true;
    customGrey: true;
    lightGrey: true;
    darkGrey: true;
    black: true;
    spinachGreen: true;
    lightOrange: true;
    lightRed: true;
    darkRed: true;
    lightGreen: true;
    darkGreen: true;
    lightBlue: true;
    lightYellow: true;
    darkYellow: true;
  }
}

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#004C97', // blue
    },
    secondary: {
      main: "rgba(255,255,255,0.7)", // white
    },
    reliablyGray: {
      main: '#666666',
    },
    requestBadge: {
      main: '#FF7F41',
      light: '#FF9A6B',
      dark: '#E65A2B',
      contrastText: '#FFFFFF',
    },
    customOrange: {
      main: '#FF6B35',
      light: '#FF8A5C',
      dark: '#CC4A1A',
      contrastText: '#FFFFFF',
    },
    customGreen: {
      main: '#4CAF50',
      light: '#81C784',
      dark: '#388E3C',
      contrastText: '#FFFFFF',
    },
    // New custom colors
    reliablyBlue: {
      main: '#004C97',
      light: '#3366B2',
      dark: '#003366',
      contrastText: '#FFFFFF',
    },
    blueGrey: {
      main: '#4D6E8D',
      light: '#6B8BA8',
      dark: '#3A5A7A',
      contrastText: '#FFFFFF',
    },
    reliablyOrange: {
      main: '#FF7F41',
      light: '#FF9A6B',
      dark: '#E65A2B',
      contrastText: '#FFFFFF',
    },
    customGrey: {
      main: '#676767',
      light: '#8A8A8A',
      dark: '#4A4A4A',
      contrastText: '#FFFFFF',
    },
    lightGrey: {
      main: '#F2F2F2',
      light: '#F8F8F8',
      dark: '#E5E5E5',
      contrastText: '#000000',
    },
    darkGrey: {
      main: '#6A6A6A',
      light: '#8D8D8D',
      dark: '#4D4D4D',
      contrastText: '#FFFFFF',
    },
    black: {
      main: '#000000',
      light: '#333333',
      dark: '#000000',
      contrastText: '#FFFFFF',
    },
    spinachGreen: {
      main: '#83B114',
      light: '#9BC234',
      dark: '#6B8F0F',
      contrastText: '#FFFFFF',
    },
    lightOrange: {
      main: '#FFE9DE',
      light: '#FFF2ED',
      dark: '#FFD6C7',
      contrastText: '#000000',
    },
    lightRed: {
      main: '#EFC7C7',
      light: '#F5D7D7',
      dark: '#E7B7B7',
      contrastText: '#000000',
    },
    darkRed: {
      main: '#B41919',
      light: '#C73A3A',
      dark: '#8F1414',
      contrastText: '#FFFFFF',
    },
    lightGreen: {
      main: '#E3EFC7',
      light: '#EDF5D7',
      dark: '#D9E7B7',
      contrastText: '#000000',
    },
    darkGreen: {
      main: '#729B13',
      light: '#8AB234',
      dark: '#5B7A0F',
      contrastText: '#FFFFFF',
    },
    lightBlue: {
      main: '#DEE6FF',
      light: '#E8EFFF',
      dark: '#D4DDFF',
      contrastText: '#000000',
    },
    lightYellow: {
      main: '#F6EDBE',
      light: '#F8F2CE',
      dark: '#F4E8AE',
      contrastText: '#000000',
    },
    darkYellow: {
      main: '#C1A03E',
      light: '#D4B55A',
      dark: '#A88A2F',
      contrastText: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: '"Source Sans Pro", "Roboto", "Helvetica", "Arial", sans-serif',
    htmlFontSize: 16,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          WebkitTextSizeAdjust: '100%',
        },
        '@media (max-width:899.95px)': {
          html: {
            fontSize: '18px',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('md')]: {
            minHeight: 44,
            fontSize: '1rem',
          },
        }),
        sizeSmall: ({ theme }) => ({
          [theme.breakpoints.down('md')]: {
            minHeight: 40,
            fontSize: '0.95rem',
            padding: '8px 14px',
          },
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('md')]: {
            fontSize: '0.95rem',
            minHeight: 52,
            padding: '12px 14px',
          },
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: ({ theme }) => ({
          [theme.breakpoints.down('md')]: {
            fontSize: '1rem',
          },
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('md')]: {
            fontSize: '1rem',
          },
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('md')]: {
            fontSize: '1rem',
            minHeight: 44,
          },
        }),
      },
    },
    MuiTypography: {
      styleOverrides: {
        body2: ({ theme }) => ({
          [theme.breakpoints.down('md')]: {
            fontSize: '1rem',
            lineHeight: 1.55,
          },
        }),
        subtitle1: ({ theme }) => ({
          [theme.breakpoints.down('md')]: {
            fontSize: '1.125rem',
            lineHeight: 1.4,
          },
        }),
        h6: ({ theme }) => ({
          [theme.breakpoints.down('md')]: {
            fontSize: '1.375rem',
          },
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('md')]: {
            padding: 10,
          },
        }),
        sizeSmall: ({ theme }) => ({
          [theme.breakpoints.down('md')]: {
            padding: 8,
            '& .MuiSvgIcon-root': {
              fontSize: '1.5rem',
            },
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        sizeSmall: ({ theme }) => ({
          [theme.breakpoints.down('md')]: {
            fontSize: '0.85rem',
            height: 26,
          },
        }),
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: ({ theme }) => ({
          [theme.breakpoints.down('md')]: {
            fontSize: '1rem',
          },
        }),
      },
    },
  },
});

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#ff5722', // deepOrange
    },
    secondary: {
      main: '#3f51b5', // indigo
    },
    requestBadge: {
      main: '#FF7F41',
      light: '#FF9A6B',
      dark: '#E65A2B',
      contrastText: '#000000',
    },
    customOrange: {
      main: '#FF6B35',
      light: '#FF8A5C',
      dark: '#CC4A1A',
      contrastText: '#000000',
    },
    customGreen: {
      main: '#4CAF50',
      light: '#81C784',
      dark: '#388E3C',
      contrastText: '#000000',
    },
    // New custom colors for dark theme
    reliablyBlue: {
      main: '#004C97',
      light: '#3366B2',
      dark: '#003366',
      contrastText: '#FFFFFF',
    },
    blueGrey: {
      main: '#4D6E8D',
      light: '#6B8BA8',
      dark: '#3A5A7A',
      contrastText: '#FFFFFF',
    },
    reliablyOrange: {
      main: '#FF7F41',
      light: '#FF9A6B',
      dark: '#E65A2B',
      contrastText: '#000000',
    },
    customGrey: {
      main: '#676767',
      light: '#8A8A8A',
      dark: '#4A4A4A',
      contrastText: '#FFFFFF',
    },
    lightGrey: {
      main: '#F2F2F2',
      light: '#F8F8F8',
      dark: '#E5E5E5',
      contrastText: '#000000',
    },
    darkGrey: {
      main: '#6A6A6A',
      light: '#8D8D8D',
      dark: '#4D4D4D',
      contrastText: '#FFFFFF',
    },
    black: {
      main: '#000000',
      light: '#333333',
      dark: '#000000',
      contrastText: '#FFFFFF',
    },
    spinachGreen: {
      main: '#83B114',
      light: '#9BC234',
      dark: '#6B8F0F',
      contrastText: '#000000',
    },
    lightOrange: {
      main: '#FFE9DE',
      light: '#FFF2ED',
      dark: '#FFD6C7',
      contrastText: '#000000',
    },
    lightRed: {
      main: '#EFC7C7',
      light: '#F5D7D7',
      dark: '#E7B7B7',
      contrastText: '#000000',
    },
    darkRed: {
      main: '#B41919',
      light: '#C73A3A',
      dark: '#8F1414',
      contrastText: '#FFFFFF',
    },
    lightGreen: {
      main: '#E3EFC7',
      light: '#EDF5D7',
      dark: '#D9E7B7',
      contrastText: '#000000',
    },
    darkGreen: {
      main: '#729B13',
      light: '#8AB234',
      dark: '#5B7A0F',
      contrastText: '#000000',
    },
    lightBlue: {
      main: '#DEE6FF',
      light: '#E8EFFF',
      dark: '#D4DDFF',
      contrastText: '#000000',
    },
    lightYellow: {
      main: '#F6EDBE',
      light: '#F8F2CE',
      dark: '#F4E8AE',
      contrastText: '#000000',
    },
    darkYellow: {
      main: '#C1A03E',
      light: '#D4B55A',
      dark: '#A88A2F',
      contrastText: '#000000',
    },
  },
});

// Helper function to get theme colors
export const getThemeColor = (theme: Theme, colorName: string) => {
  const palette = theme.palette as any;
  return palette?.[colorName]?.main || '#000000';
};

export default lightTheme;
